import { isEmergingTerm } from "@ow/shared";
import type { ReactNode } from "react";
import { SHADOW, cardBase } from "./card";
import type { EmergingTerm } from "./data";
import { Dropdown, type DropdownOption } from "./dropdown";
import { Flag } from "./flag";
import { pct, tidyText, titleCase, withUnit } from "./helpers";
import { HeroBand, type HeroBandMetric } from "./hero-row";
import { SegToggle } from "./seg-toggle";
import { C, FM, FS, FU, T, W } from "./tokens";

// Tasteful empty/loading panel shown in place of a data viz when the live
// API has no rows yet (e.g. no successful cron tick). Replaces silent
// fall-through to fictional demo data so the dashboard never presents mock
// numbers as real.
export function EmptyPanel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: "32px 24px",
        border: `1px dashed ${C.border}`,
        borderRadius: 8,
        fontFamily: FU,
        fontSize: T.body,
        color: C.textMd,
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}

interface TimePeriodDropdownProps {
  value: string;
  onChange: (v: string) => void;
  options?: DropdownOption[];
}

const TIME_PERIOD_OPTIONS: DropdownOption[] = [
  { value: "1m", label: "Last 1 month" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "1y", label: "Last 1 year" },
  { value: "2y", label: "Last 2 years" },
  { value: "5y", label: "Last 5 years" },
];

// For sections wired to /search-signals + /booking-intent — the backend
// supports 3M/6M/1Y/2Y/5Y only, so "Last 1 month" is dropped.
export const SIGNAL_TIME_PERIOD_OPTIONS: DropdownOption[] = TIME_PERIOD_OPTIONS.filter((o) => o.value !== "1m");

export function TimePeriodDropdown({ value, onChange, options = TIME_PERIOD_OPTIONS }: TimePeriodDropdownProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
      <span
        style={{
          fontFamily: FU,
          fontSize: T.micro,
          fontWeight: W.semibold,
          color: C.textMd,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        Time period
      </span>
      <Dropdown value={value} onChange={onChange} options={options} minWidth={220} />
    </div>
  );
}

export interface SignalKpi {
  label: string;
  value: number | string;
  unit: string;
  color: string;
  note: string;
}

interface SignalBarProps {
  tiles: SignalKpi[];
  marginBottom?: number;
}

export function SignalBar({ tiles, marginBottom = 44 }: SignalBarProps) {
  // Render through the shared navy HeroBand (same navy stat band as Global 1.1 /
  // Saudi 1.1) rather than a bespoke translucent-glass strip, so every signal
  // section's summary reads as one product. An adapter, not a fork.
  //
  // NEUTRAL by rule: the affix stays the band's default muted ink — NOT a
  // per-tile green/red/purple. Every signal-summary strip in the workspace must
  // look identical (same component, same hierarchy), and ad-hoc tri-colour reads
  // as arbitrary. `SignalKpi.color` is kept on the type for callers but the strip
  // ignores it; semantic colour lives in the data viz below, not the count strip.
  // `collapse={false}` keeps all three counts on one row at every width.
  const metrics: HeroBandMetric[] = tiles.map((t, i) => {
    const numeric = typeof t.value === "number";
    return {
      key: `${t.label}-${i}`,
      label: t.label,
      value: numeric ? (t.value as number) : 0,
      format: numeric ? undefined : () => String(t.value),
      unit: t.unit,
      note: { text: t.note },
    };
  });
  return (
    <div style={{ marginBottom }}>
      <HeroBand metrics={metrics} headMinHeight={40} collapse={false} />
    </div>
  );
}

// The one compact time-period control for every signal section — the shared
// SegToggle (3M · 6M · 1Y · 2Y · 5Y) sized for the command bar's 36px actions
// slot, in its `elevated` (opaque, higher-contrast) variant so it stays legible
// on the sticky observatory band, which is transparent at the top of the page.
// A small mono "PERIOD" eyebrow sits to its left so it reads unmistakably as the
// (global, all-views) time filter and stays distinct from the section rail
// beneath it. Replaces the heavy standalone PeriodBar and the oversized 220px
// TimePeriodDropdown, so every section's period switch is the same control at the
// same height.
const PERIOD_SHORT: Record<string, string> = { "3m": "3M", "6m": "6M", "1y": "1Y", "2y": "2Y", "5y": "5Y" };
export const PERIOD_SEG_OPTIONS = SIGNAL_TIME_PERIOD_OPTIONS.map((o) => ({
  key: o.value,
  label: PERIOD_SHORT[o.value] ?? o.value,
}));

export function PeriodSeg({
  value,
  onChange,
  accent,
}: {
  value: string;
  onChange: (v: string) => void;
  accent?: string;
}) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
      <span
        style={{
          fontFamily: FM,
          fontSize: 10,
          fontWeight: W.medium,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: C.textLo,
          whiteSpace: "nowrap",
        }}
      >
        Period
      </span>
      <SegToggle options={PERIOD_SEG_OPTIONS} value={value} onChange={onChange} accent={accent} elevated />
    </div>
  );
}

interface HeatRow {
  iso: string;
  name: string;
  region: string;
  // Optional per-tile series for the inline sparkline.
  sparkline?: number[];
  [key: string]: unknown;
}

// Tiny inline trend chart drawn as a single SVG polyline. Scales the series
// to its own min/max so even flat-ish data shows shape; stroke inherits the
// tile's accent color.
export function Sparkline({ points, color }: { points: number[]; color: string }) {
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 26;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((p - min) / range) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ width: "100%", height: 26, display: "block", overflow: "visible" }}
    >
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

interface HeatBoardProps<T extends HeatRow> {
  data: T[];
  valKey: keyof T;
  // Show a flag emoji next to each destination name.
  showFlags?: boolean;
  // When set, render only the top N and bottom N rows (by valKey) instead of
  // the full list — keeps the board scannable when the source set is large.
  topBottom?: number;
  // Tiles per row. Defaults to 6.
  columns?: number;
}

export function HeatBoard<T extends HeatRow>({
  data,
  valKey,
  showFlags = false,
  topBottom,
  columns = 6,
}: HeatBoardProps<T>) {
  const sorted = [...data].sort((a, b) => Number(b[valKey]) - Number(a[valKey]));
  const isSliced = !!(topBottom && sorted.length > topBottom * 2);
  const shown = isSliced ? [...sorted.slice(0, topBottom), ...sorted.slice(-topBottom)] : sorted;
  // In the top/bottom split view, the bottom group gets a yellow (gold) wash
  // so it reads as a distinct band from the top group.
  const bottomStart = isSliced ? (topBottom as number) : Number.POSITIVE_INFINITY;
  const goldTile = { bg: C.goldLight, border: "rgba(255,184,0,0.40)", text: C.goldText };
  const colorFor = (m: number) => {
    // Green denotes growth, red denotes decline; intensity scales with magnitude.
    if (m >= 25) return { bg: "rgba(47,117,0,0.14)", border: "rgba(47,117,0,0.34)", text: C.emerging };
    if (m >= 10) return { bg: "rgba(47,117,0,0.07)", border: "rgba(47,117,0,0.22)", text: C.emerging };
    if (m >= 0) return { bg: C.surface, border: C.border, text: C.textHi };
    if (m >= -10) return { bg: "rgba(197,53,50,0.07)", border: "rgba(197,53,50,0.22)", text: C.crisis };
    return { bg: "rgba(197,53,50,0.14)", border: "rgba(197,53,50,0.34)", text: C.crisis };
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 10 }}>
      {shown.map((d, idx) => {
        const v = Number(d[valKey]);
        const c = idx >= bottomStart ? goldTile : colorFor(v);
        return (
          <div
            key={d.iso}
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              boxShadow: SHADOW.rest,
              padding: "14px 16px",
              minHeight: 116,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontFamily: FU,
                fontSize: T.lead,
                fontWeight: W.semibold,
                color: C.textHi,
                lineHeight: 1.2,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {showFlags ? <Flag iso3={d.iso} width={18} style={{ flexShrink: 0 }} /> : null}
              <span>{d.name}</span>
            </div>
            {d.sparkline && d.sparkline.length >= 2 ? (
              <div style={{ marginTop: "auto", paddingTop: 14 }}>
                <Sparkline points={d.sparkline} color={c.text} />
              </div>
            ) : (
              <div style={{ marginTop: "auto" }} />
            )}
            <div
              style={{
                fontFamily: FS,
                fontSize: T.title,
                fontWeight: W.light,
                color: c.text,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                whiteSpace: "nowrap",
                paddingTop: 10,
                borderTop: `1px solid ${c.border}`,
                marginTop: 8,
              }}
            >
              {pct(v)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface EmergingWallProps {
  terms: EmergingTerm[];
}

export function EmergingWall({ terms }: EmergingWallProps) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
      {[...terms]
        // Emerging = growing only; a declining term rendered a green "+" on a
        // negative number. Drop non-growers before ranking.
        .filter(isEmergingTerm)
        .sort((a, b) => b.markets.length - a.markets.length || b.growth - a.growth)
        .map((t) => (
          <div key={t.term} style={{ ...cardBase, padding: "22px 24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 14,
                gap: 12,
              }}
            >
              <div
                style={{
                  fontFamily: FU,
                  fontSize: T.head,
                  fontWeight: W.semibold,
                  color: C.textHi,
                  lineHeight: 1.25,
                }}
              >
                "{titleCase(t.term)}"
              </div>
              <div
                style={{
                  fontFamily: FU,
                  fontSize: T.lead,
                  fontWeight: W.semibold,
                  color: t.growth >= 0 ? C.emerging : C.crisis,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {t.growth >= 0 ? "+" : ""}
                {withUnit(t.growth, "%")}
              </div>
            </div>
            <div style={{ fontFamily: FU, fontSize: T.cap, color: C.textMd, lineHeight: 1.55, marginBottom: 14 }}>
              {tidyText(t.note)}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                paddingTop: 12,
                borderTop: `1px solid ${C.border}`,
              }}
            >
              <span
                style={{
                  fontFamily: FU,
                  fontSize: T.tag,
                  fontWeight: W.semibold,
                  color: C.textMd,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Driven by
              </span>
              <span style={{ fontFamily: FU, fontSize: T.cap, color: C.textHi, fontWeight: W.medium }}>
                {t.markets.join(" · ")}
              </span>
            </div>
          </div>
        ))}
    </div>
  );
}

/**
 * The destinations a momentum board publishes but does not rank.
 *
 * Every tracked destination now carries a real relative volume — there is no
 * size floor any more. What is left is a confidence split: where the ladder's
 * bottom rung still read under its precision floor, the volume is real but the
 * momentum % is a small-denominator number, so the destination is NAMED here
 * rather than ranked among the movers. Renders nothing when the run resolved
 * every destination cleanly, which is the usual case.
 */
export function LowVolumeNote({ rows }: { rows: Array<{ iso: string; name: string }> }) {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 14, fontFamily: FU, fontSize: T.cap, color: C.textMd, lineHeight: 1.6 }}>
      <span style={{ fontWeight: W.semibold, color: C.textHi }}>Published, not ranked ({rows.length}):</span>
      <span> {rows.map((r) => r.name).join(" \u00b7 ")}</span>
      <span style={{ display: "block", marginTop: 4 }}>
        Their search volume is measured and shown, but it sits at the bottom of the ladder's resolution, so a
        period-over-period % on that base is noise rather than movement.
      </span>
    </div>
  );
}
